import { getLastItem } from '../../utils/array';
import { isMobileBrowser } from '../../utils/browser';

// list of exact URLs to hide navigation for
const hideNavigationForURLs = [
  '/checkout/address',
  '/checkout/payment',
  '/checkout/thank-you',
  '/login',
  '/mtm-preorder',
];

// list of URL prefixes to hide navigation for
// cart is in prefix check because it could either be /cart or /cart/edit/0
const hideNavigationForURLPrefixes = ['reset-password', 'cart'];

// list of URL suffixes to hide navigation for
const hideNavigationForURLSuffixes = ['customize'];

/**
 * Returns true if navigation (header / footer) should be rendered
 *
 * Navigation should be hidden for two sets of URLS.
 * hideNavigationForURLs are full exact URLs to match
 * hideNavigationForURLPrefixes are pattern based. If the URL starts with the given prefix, it is considered a match
 * @param {History} history
 * @returns Boolean
 */
export const shouldShowNavigation = (history) => {
  // If current location is found in given list of exact URLs, hide navigation
  const hideExact =
    hideNavigationForURLs.indexOf(history?.location?.pathname) > -1;
  if (hideExact) return false;

  // If prefix of current location is found in given list of URL prefixes, hide navigation
  const prefix = history?.location?.pathname.split('/')[1];
  const hidePrefix = hideNavigationForURLPrefixes.indexOf(prefix) > -1;
  if (hidePrefix) return false;

  // If suffix of current location is found in given list of URL prefixes, hide navigation
  const suffix = getLastItem(history?.location?.pathname.split('/'));
  const hideSuffix = hideNavigationForURLSuffixes.indexOf(suffix) > -1;
  if (hideSuffix) return false;

  return true;
};

// By default, we show the dark hamburger menu icon. Here we choose the URLs where we want the light icon
const mobileLightHamburgerURLs = [
  '/',
  '/shop/jackets/teba-jacket',
  '/shop/trousers/office-trousers',
  '/shop/shirts/cooper-collar-beach-shirt',
  '/shop/coats/dress-coat',
  '/shop/shirts/dinner-shirt',
  '/shop/trousers/flat-front-dinner-trousers',
  '/shop/suits/single-breasted-dinner-suit',
  '/shop/suits/double-breasted-dinner-suit',
  '/shop/jackets/single-breasted-dinner-jacket',
  '/shop/jackets/shawl-lapel-double-breasted-dinner-jacket',
  '/shop/waistcoats/single-breasted-dinner-waistcoat',
  '/shop/trousers/pleated-dinner-trousers',
];
const desktopLightHamburgerURLs = [
  '/shop/suits/double-breasted-suit',
  '/shop/jackets/single-breasted-jacket',
  '/shop/shirts/pop-over-polo-shirt',
  '/shop/shirts/cooper-collar-beach-shirt',
  '/shop/coats/dress-coat',
  '/shop/shirts/dinner-shirt',
  '/shop/trousers/flat-front-dinner-trousers',
  '/shop/suits/single-breasted-dinner-suit',
  '/shop/suits/double-breasted-dinner-suit',
  '/shop/jackets/single-breasted-dinner-jacket',
  '/shop/jackets/shawl-lapel-double-breasted-dinner-jacket',
  '/shop/waistcoats/single-breasted-dinner-waistcoat',
  '/shop/trousers/pleated-dinner-trousers',
];
const lightHamburgerURLs = isMobileBrowser()
  ? mobileLightHamburgerURLs
  : desktopLightHamburgerURLs;
export const shouldShowHamburgerLight = (history) => {
  const showHamburgerLight =
    lightHamburgerURLs.indexOf(history?.location?.pathname) > -1;
  return showHamburgerLight;
};

// By default, we show the dark AO logo. Here we choose the URLs where we want the light icon
const mobileLightBrandURLs = [
  '/shop/jackets/teba-jacket',
  '/shop/shirts/cooper-collar-beach-shirt',
  '/shop/coats/dress-coat',
  '/shop/trousers/flat-front-dinner-trousers',
  '/shop/suits/single-breasted-dinner-suit',
  '/shop/suits/double-breasted-dinner-suit',
  '/shop/jackets/single-breasted-dinner-jacket',
  '/shop/jackets/shawl-lapel-double-breasted-dinner-jacket',
  '/shop/waistcoats/single-breasted-dinner-waistcoat',
];
const desktopLightBrandURLs = [
  '/',
  '/shop/shirts/cooper-collar-beach-shirt',
  '/shop/coats/dress-coat',
  '/shop/shirts/dinner-shirt',
  '/shop/trousers/flat-front-dinner-trousers',
  '/shop/suits/single-breasted-dinner-suit',
  '/shop/suits/double-breasted-dinner-suit',
  '/shop/jackets/single-breasted-dinner-jacket',
  '/shop/jackets/shawl-lapel-double-breasted-dinner-jacket',
  '/shop/waistcoats/single-breasted-dinner-waistcoat',
  '/shop/trousers/pleated-dinner-trousers',
];
const lightBrandURLs = isMobileBrowser()
  ? mobileLightBrandURLs
  : desktopLightBrandURLs;
export const shouldShowBrandLight = (history) => {
  const showBrandLight =
    lightBrandURLs.indexOf(history?.location?.pathname) > -1;
  return showBrandLight;
};

// By default, we show the dark cart icon. Here we choose the URLs where we want the light icon
const mobileLightCartURLs = [
  '/shop/jackets/teba-jacket',
  '/shop/shirts/pop-over-polo-shirt',
  '/shop/shirts/cooper-collar-beach-shirt',
  '/shop/coats/dress-coat',
  '/shop/suits/single-breasted-dinner-suit',
  '/shop/suits/double-breasted-dinner-suit',
  '/shop/jackets/single-breasted-dinner-jacket',
  '/shop/jackets/shawl-lapel-double-breasted-dinner-jacket',
  '/shop/waistcoats/single-breasted-dinner-waistcoat',
];
const desktopLightCartURLs = [
  '/',
  '/shop/suits/double-breasted-suit',
  '/shop/shirts/pop-over-polo-shirt',
  '/shop/shirts/cooper-collar-beach-shirt',
  '/shop/shirts/dinner-shirt',
  '/shop/trousers/flat-front-dinner-trousers',
  '/shop/suits/single-breasted-dinner-suit',
  '/shop/suits/double-breasted-dinner-suit',
  '/shop/jackets/single-breasted-dinner-jacket',
  '/shop/jackets/shawl-lapel-double-breasted-dinner-jacket',
  '/shop/trousers/pleated-dinner-trousers',
];
const lightCartURLs = isMobileBrowser()
  ? mobileLightCartURLs
  : desktopLightCartURLs;
export const shouldShowCartLight = (history) => {
  const showCartLight = lightCartURLs.indexOf(history?.location?.pathname) > -1;
  return showCartLight;
};
